import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { AtSign } from 'lucide-react';
import { teamMembersService } from '@services/team-members.service';

// #18935 — @mention autocomplete for internal notes. The note composer never
// had a mention picker, and a hand-typed "@name" reached no one. This hook adds
// a teammate autocomplete to the contentEditable composer and records who was
// actually picked, so the caller can forward them as mention notifications.

export interface MentionUser {
  id: string;
  name: string;
  email?: string;
}

export interface CollectedMention {
  userId: string;
  userName: string;
}

/** A teammate's display name, with sensible fallbacks. */
function memberLabel(m: {
  displayName?: string;
  firstName?: string;
  lastName?: string;
  email: string;
}): string {
  return (
    m.displayName?.trim() ||
    `${m.firstName ?? ''} ${m.lastName ?? ''}`.trim() ||
    m.email
  );
}

/** Most teammates rendered in the @ list at once (it scrolls). */
const MAX_MENTION_ITEMS = 50;

/**
 * Every user in the organisation who can be mentioned or emailed: active and
 * pending (invited, not yet signed in); the server leaves out deactivated and
 * deleted users. Uses the directory endpoint, which — unlike the admin-only
 * team-members list — every agent can read, and which is not page-capped.
 */
export async function loadOrganisationUsers(): Promise<MentionUser[]> {
  const res = await teamMembersService.getDirectory();
  const seen = new Set<string>();
  const all: MentionUser[] = [];
  for (const m of res?.data || []) {
    if (!m?.id || seen.has(m.id)) continue;
    seen.add(m.id);
    all.push({ id: m.id, name: memberLabel(m), email: m.email });
  }
  return all.sort((a, b) => a.name.localeCompare(b.name));
}

/**
 * Wires an `@`-mention autocomplete onto a contentEditable editor.
 *
 * Detection is caret-based: when the text immediately before a collapsed caret
 * ends in `@<query>` (query may be empty right after the `@`), the dropdown
 * opens filtered by that query. Selecting a teammate replaces the `@<query>`
 * token with `@Display Name ` and remembers the pick.
 *
 * `enabled` gates the whole thing (teammate loading included).
 */
export function useMentionAutocomplete(
  editorRef: React.RefObject<HTMLElement>,
  onAfterInsert: () => void,
  enabled: boolean,
) {
  const [users, setUsers] = useState<MentionUser[]>([]);
  // null → closed. '' → open with no query yet (just typed "@").
  const [query, setQuery] = useState<string | null>(null);
  const [activeIndex, setActiveIndex] = useState(0);
  // userId → the display name as inserted. Kept across edits so we can reconcile
  // against the final text on submit (a deleted @name should not notify).
  const picked = useRef<Map<string, string>>(new Map());

  // Load every organisation user once the composer enables mentions.
  // Best-effort: on failure the dropdown simply never has anything to show.
  useEffect(() => {
    if (!enabled || users.length > 0) return;
    let cancelled = false;
    loadOrganisationUsers()
      .then((list) => {
        if (!cancelled) setUsers(list);
      })
      .catch(() => {
        /* non-fatal — mentions just won't autocomplete */
      });
    return () => {
      cancelled = true;
    };
  }, [enabled, users.length]);

  const items = useMemo(() => {
    if (query === null) return [];
    const q = query.toLowerCase();
    return users
      .filter(
        (u) =>
          u.name.toLowerCase().includes(q) || (u.email ?? '').toLowerCase().includes(q),
      )
      // The list scrolls; the cap only keeps a very large organisation's
      // un-narrowed "@" fast to render. Typing narrows it.
      .slice(0, MAX_MENTION_ITEMS);
  }, [query, users]);

  const close = useCallback(() => setQuery(null), []);

  // Inspect the caret and open/close the dropdown accordingly.
  const detect = useCallback(() => {
    if (!enabled) return;
    const sel = window.getSelection();
    if (!sel || sel.rangeCount === 0) return close();
    const range = sel.getRangeAt(0);
    if (!range.collapsed) return close();
    const node = range.startContainer;
    if (node.nodeType !== Node.TEXT_NODE) return close();

    const before = (node.textContent || '').slice(0, range.startOffset);
    // `@` must start the string or follow whitespace; query is name-ish chars.
    const m = before.match(/(?:^|\s)@([\p{L}\p{N}._' -]{0,40})$/u);
    if (m) {
      setQuery(m[1]);
      setActiveIndex(0);
    } else {
      close();
    }
  }, [enabled, close]);

  const insert = useCallback(
    (user: MentionUser) => {
      const sel = window.getSelection();
      if (!sel || sel.rangeCount === 0) return;
      const range = sel.getRangeAt(0);
      const node = range.startContainer;
      if (node.nodeType !== Node.TEXT_NODE) return;

      const cursor = range.startOffset;
      const before = (node.textContent || '').slice(0, cursor);
      const at = before.lastIndexOf('@');
      if (at < 0) return;

      // Select from the `@` to the caret, then overwrite with the mention so the
      // edit lands on the browser undo stack and the caret ends after it.
      const replaceRange = document.createRange();
      replaceRange.setStart(node, at);
      replaceRange.setEnd(node, cursor);
      sel.removeAllRanges();
      sel.addRange(replaceRange);
      document.execCommand('insertText', false, `@${user.name} `);

      picked.current.set(user.id, user.name);
      close();
      onAfterInsert();
    },
    [close, onAfterInsert],
  );

  // Arrow/Enter/Tab/Escape while the dropdown is open. Registered as a capturing
  // listener so it runs before the editor's own Enter handling.
  const handleKeyDown = useCallback(
    (e: KeyboardEvent) => {
      if (query === null || items.length === 0) return;
      if (e.key === 'ArrowDown') {
        e.preventDefault();
        setActiveIndex((i) => (i + 1) % items.length);
      } else if (e.key === 'ArrowUp') {
        e.preventDefault();
        setActiveIndex((i) => (i - 1 + items.length) % items.length);
      } else if (e.key === 'Enter' || e.key === 'Tab') {
        e.preventDefault();
        insert(items[activeIndex] ?? items[0]);
      } else if (e.key === 'Escape') {
        e.preventDefault();
        close();
      }
    },
    [query, items, activeIndex, insert, close],
  );

  useEffect(() => {
    const el = editorRef.current;
    if (!el || !enabled) return;
    el.addEventListener('keyup', detect);
    el.addEventListener('mouseup', detect);
    el.addEventListener('keydown', handleKeyDown, true);
    return () => {
      el.removeEventListener('keyup', detect);
      el.removeEventListener('mouseup', detect);
      el.removeEventListener('keydown', handleKeyDown, true);
    };
  }, [editorRef, enabled, detect, handleKeyDown]);

  /**
   * The mentions to send with the note: only those whose inserted `@Name` still
   * appears in the final text, so removing a name before sending removes the
   * notification too. Deduplicated by userId.
   */
  const collectMentions = useCallback((text: string): CollectedMention[] => {
    const out: CollectedMention[] = [];
    picked.current.forEach((name, userId) => {
      if (name && text.includes(`@${name}`)) out.push({ userId, userName: name });
    });
    return out;
  }, []);

  /** Clear remembered picks (call after a successful send). */
  const reset = useCallback(() => {
    picked.current.clear();
    close();
  }, [close]);

  return {
    open: query !== null && items.length > 0,
    /** Every active teammate (also used for To/Cc suggestions). */
    users,
    items,
    activeIndex,
    setActiveIndex,
    insert,
    collectMentions,
    reset,
  };
}

interface TeammateListProps {
  /** Small caption above the list. */
  heading: string;
  items: MentionUser[];
  activeIndex: number;
  onHover: (index: number) => void;
  onSelect: (user: MentionUser) => void;
  /** Positioning classes (the list is absolutely positioned by its parent). */
  className?: string;
  /** For aria wiring from an input (`aria-controls`). */
  id?: string;
}

/**
 * A floating list of teammates, styled like the composer. Shared by the
 * @mention picker (editor) and the To/Cc suggestions (RecipientInput).
 * Items keep focus where it is on mousedown, so the editor selection / input
 * focus survive a click.
 */
export function TeammateList({
  heading,
  items,
  activeIndex,
  onHover,
  onSelect,
  className,
  id,
}: TeammateListProps) {
  // Keep the keyboard-highlighted row visible as arrows move through a long list.
  const listRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const row = listRef.current?.querySelector<HTMLElement>(`[data-index="${activeIndex}"]`);
    row?.scrollIntoView?.({ block: 'nearest' });
  }, [activeIndex]);

  return (
    <div
      id={id}
      role="listbox"
      aria-label={heading}
      className={`absolute z-50 max-h-[240px] w-72 max-w-[calc(100vw-32px)] overflow-y-auto border border-[var(--co-line)] bg-[var(--co-raised)] shadow-[0_12px_32px_rgba(35,32,28,.14)] ${className ?? ''}`}
    >
      <div className="flex items-center gap-1.5 border-b border-[var(--co-line-subtle)] px-3 py-[7px] font-mono text-[10px] uppercase tracking-[.12em] text-[var(--co-ink-4)]">
        <AtSign className="h-3 w-3" />
        {heading}
      </div>
      <div ref={listRef} className="py-1">
        {items.map((u, i) => (
          <button
            key={u.id}
            data-index={i}
            type="button"
            role="option"
            aria-selected={i === activeIndex}
            onMouseDown={(e) => e.preventDefault()}
            onMouseEnter={() => onHover(i)}
            onClick={() => onSelect(u)}
            className={`flex w-full items-center gap-2.5 px-3 py-[6px] text-left transition-colors duration-[120ms] ${
              i === activeIndex ? 'bg-[var(--co-brass-wash)]' : 'hover:bg-[var(--co-brass-wash)]'
            }`}
          >
            <span
              aria-hidden="true"
              className="flex h-6 w-6 flex-none items-center justify-center border border-[var(--co-line)] font-mono text-[10.5px] font-semibold text-[var(--co-ink-3)]"
            >
              {u.name.charAt(0).toUpperCase()}
            </span>
            <span className="min-w-0 flex-1">
              <span className="block truncate text-[12.5px] text-[var(--co-ink)]">{u.name}</span>
              {u.email && (
                <span className="block truncate font-mono text-[10.5px] text-[var(--co-ink-4)]">
                  {u.email}
                </span>
              )}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}

interface MentionDropdownProps {
  open: boolean;
  items: MentionUser[];
  activeIndex: number;
  onHover: (index: number) => void;
  onSelect: (user: MentionUser) => void;
}

/** The @mention suggestion list, anchored above the composer content. */
export function MentionDropdown({
  open,
  items,
  activeIndex,
  onHover,
  onSelect,
}: MentionDropdownProps) {
  if (!open) return null;
  return (
    <TeammateList
      heading="Mention a teammate"
      items={items}
      activeIndex={activeIndex}
      onHover={onHover}
      onSelect={onSelect}
      className="bottom-full left-0 mb-1"
    />
  );
}
